import { useContext, useEffect, useId, useRef, useState } from 'react';
import { SourceContext } from '../app/auth';
import { Info } from 'lucide-react';
import { useSearchParams } from 'react-router';
export function useChoice(key: string, fallback: string, options: readonly string[]) {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value = raw && options.includes(raw) ? raw : fallback;
  return [value, (next: string) => setParams(previous => {
    const result = new URLSearchParams(previous);
    if (next === fallback) result.delete(key); else result.set(key, next);
    return result;
  })] as const;
}
export function Segment({ label, value, options, onChange }: { label: string; value: string; options: readonly (readonly [string, string])[]; onChange: (value: string) => void }) {
  return <div className="seg" role="group" aria-label={label}>{options.map(([key, name]) => <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}>{name}</button>)}</div>;
}
export function Definition({ title, definition, calculation, note, display, sourceText, updateText }: { sourceText?: string; updateText?: string; title: string; definition: string; calculation: string; note: string; display: string }) {
  const source = useContext(SourceContext);
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  function position() {
    const trigger = button.current!, popup = panel.current!;
    const rect = trigger.getBoundingClientRect();
    const tokens = getComputedStyle(popup);
    const gap = parseFloat(tokens.getPropertyValue('--sp-2'));
    const edge = parseFloat(tokens.getPropertyValue('--sp-4'));
    const width = popup.offsetWidth, height = popup.offsetHeight;
    const left = Math.max(edge, Math.min(rect.left, innerWidth - width - edge));
    const below = rect.bottom + gap;
    const top = below + height <= innerHeight - edge ? below : Math.max(edge, rect.top - gap - height);
    popup.style.left = left + 'px';
    popup.style.top = top + 'px';
  }
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => {
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };
  }, [open]);
  return <><button className="info-btn" ref={button} popoverTarget={id} aria-label={title + '的口径'} aria-expanded={open} onClick={event => {
    // 原生 toggle 事件会延后派发，先同步打开并定位，避免第一次绘制闪到左上角。
    event.preventDefault();
    panel.current!.togglePopover();
    if (panel.current!.matches(':popover-open')) position();
  }}><Info className="ico"/></button>
    <div id={id} ref={panel} popover="auto" className="pop-panel def metric-definition" onToggle={event => {
      if (event.newState === 'open') position();
      setOpen(event.newState === 'open');
      if (event.newState === 'closed') button.current?.focus({ preventScroll: true });
    }}><div className="def-t">{title}</div><dl>
      <dt>定义</dt><dd>{definition}</dd><dt>算法</dt><dd>{calculation}</dd>
      <dt>展示</dt><dd>{display}</dd>
      <dt>来源</dt><dd>{sourceText ?? (source?.source === 'measured' ? '真实设备聚合数据' : '合成演示数据，未连接真实设备')}</dd><dt>更新</dt><dd>{updateText ?? (source?.source === 'measured' ? '统计截至 ' + source.asOf + '；采集于 ' + source.observedAt : '固定截至 2026-10-05，不自动更新')}</dd><dt>注意</dt><dd>{note}</dd>
    </dl></div></>;
}
export const number = (value: number | null) => value === null || !Number.isFinite(value) ? '—' : new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 1 }).format(value);
